import React from 'react';
import type { StoredInvestigationRun } from '../types';
import { normalizeSreTimestamp } from '../utils/formatLocalDateTime';
import { useDateFormatter } from '../../../hooks/useDateFormatter';

export interface SreInvestigatedBadgeProps {
  investigated: boolean;
  loading?: boolean;
  latest?: StoredInvestigationRun | null;
  runCount?: number;
  onClick?: () => void;
  className?: string;
}

/**
 * Default-view CTA when a completed SRE investigation exists (screenshot style).
 */
const SreInvestigatedBadge: React.FC<SreInvestigatedBadgeProps> = ({
  investigated,
  loading = false,
  latest,
  runCount = 0,
  onClick,
  className = '',
}) => {
  const { formatDateTime } = useDateFormatter();

  if (loading && !investigated) {
    return (
      <span
        className={[
          'inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50',
          'px-3.5 py-2 text-sm font-medium text-slate-500',
          className,
        ].join(' ')}
        aria-busy="true"
      >
        Checking…
      </span>
    );
  }

  if (!investigated) {
    return null;
  }

  const summary =
    latest?.rootCauseSummary ||
    latest?.report?.root_cause?.summary ||
    latest?.status ||
    'completed';
  const when = formatDateTime(
    normalizeSreTimestamp(latest?.completedAt || latest?.startedAt),
    true
  );
  const whenLabel = when === '-' ? '' : when;
  const title = [
    'View latest SRE investigation',
    whenLabel ? `Last run: ${whenLabel}` : null,
    summary ? `Summary: ${summary}` : null,
    runCount > 1 ? `${runCount} runs on record` : null,
  ]
    .filter(Boolean)
    .join('\n');

  const commonClass = [
    'inline-flex items-center gap-2 rounded-lg',
    'border border-emerald-300 bg-emerald-50',
    'px-3.5 py-2 text-sm font-semibold text-emerald-800',
    'shadow-[0_0_2px_rgba(6,95,70,0.25)]',
    className,
  ].join(' ');

  const content = (
    <>
      <span
        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-700"
        aria-hidden
      >
        <svg
          viewBox="0 0 12 12"
          className="h-2.5 w-2.5 text-white"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2.5 6.2 4.8 8.5 9.5 3.5" />
        </svg>
      </span>
      <span>Investigated</span>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        aria-label="View latest SRE investigation"
        className={[
          commonClass,
          'transition hover:bg-emerald-100 hover:border-emerald-400',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-1',
        ].join(' ')}
      >
        {content}
      </button>
    );
  }

  return (
    <span className={commonClass} title={title}>
      {content}
    </span>
  );
};

export default SreInvestigatedBadge;
